import { Dropdown as NextDropdown, Loading } from '@nextui-org/react';
import React, { Key, ReactNode, useMemo } from 'react';

import { CheckIcon, ChevronIcon } from '../../assets';
import Badge from '../FilterBadge';
import HelpTooltip from '../HelpTooltip';
import {
  CheckboxIndicatorBox,
  Container,
  DropDownButton,
  DropdownError,
  DropDownItemContainer,
  DropDownItemDescription,
  DropDownItemLeftContainer,
  DropDownItemText,
  DropDownMenu,
  DropDownValueText,
  Separator,
  StyledDropdown,
  Title,
} from './Dropdown.styles';
import useDropdown from './useDropdown';

export interface DropdownItemProps<T> {
  key?: string;
  text?: string;
  value?: T;
  description?: string;
  selected?: boolean;
  icon?: ReactNode;
  disabled?: boolean;
  rightIcon?: ReactNode;
}

export type DropdownSelection = number | number[];

export interface DropDownProps<T = any> extends React.ComponentPropsWithoutRef<typeof Container> {
  title?: string;
  items?: DropdownItemProps<T>[];
  selectedIndex?: number;
  selectedIndexes?: number[];
  placeholder?: string;
  multiselect?: boolean;
  helpText?: string;
  disabled?: boolean;
  hideBorder?: boolean;
  disabledKeys?: Key[];
  customTrigger?: ReactNode;
  onSelectionChange?: (selection: DropdownSelection) => void;
  loading?: boolean;
  disallowEmptySelection?: boolean;
  error?: string;
  required?: boolean;
  showBadge?: boolean;
  placeholderIcon?: ReactNode;
  showSelectedValue?: boolean;
  autoMenuWidth?: boolean;
  putSelectedFirst?: boolean;
  descriptionOnBottom?: boolean;
  shouldShowDescriptionWithValue?: boolean;
  hideChevron?: boolean;
  showCheckboxIndicator?: boolean;
}

const Dropdown = (props: DropDownProps) => {
  const {
    items,
    title,
    selectedIndex,
    selectedIndexes,
    placeholder,
    multiselect,
    helpText,
    disabled,
    disabledKeys,
    customTrigger,
    hideBorder,
    disallowEmptySelection = !multiselect,
    onSelectionChange,
    loading = false,
    error = '',
    required = false,
    showBadge,
    placeholderIcon,
    showSelectedValue = true,
    putSelectedFirst = false,
    descriptionOnBottom = false,
    shouldShowDescriptionWithValue = false,
    hideChevron = false,
    showCheckboxIndicator = false,
    ...rest
  } = props;
  const {
    selected,
    setSelected,
    selectedValue,
    selectedIcon,
    disabledKeysLocal,
    buttonRef,
    buttonWidth,
    selectedDescription,
    selectedRightIcon,
  } = useDropdown(props);

  const onLocalSelectionChange = (keys: 'all' | Set<Key>) => {
    // react-aria collapses the selection to the literal 'all' once every item ends up checked
    // (not just from a dedicated "select all" control), resolve it to the full key set instead
    // of dropping the update, or a multiselect with few items silently stops reflecting clicks
    // the moment the last item gets checked.
    const resolvedKeys = keys === 'all' ? new Set(items.map((_, index) => index.toString())) : keys;

    setSelected(resolvedKeys);
    const nextIndexes = Array.from(resolvedKeys || []).map((key) => Number(key));
    onSelectionChange?.(multiselect ? nextIndexes : nextIndexes[0]);
  };

  const sortedItems = useMemo(() => {
    if (!putSelectedFirst || !selected) return items;

    const currentIndexes = Array.from(selected).map(Number);
    return [...items].sort((a, b) => {
      const aIsSelected = currentIndexes.includes(items.indexOf(a));
      const bIsSelected = currentIndexes.includes(items.indexOf(b));
      if (aIsSelected === bIsSelected) return 0;
      return aIsSelected ? -1 : 1;
    });
  }, [items, selected, putSelectedFirst]);

  const dropDownComponent = useMemo(
    () => (
      <DropDownMenu
        width={!props.autoMenuWidth && buttonWidth}
        selectionMode={multiselect ? 'multiple' : 'single'}
        disallowEmptySelection={disallowEmptySelection}
        selectedKeys={selected}
        disabledKeys={disabledKeysLocal}
        onSelectionChange={onLocalSelectionChange}
        $showCheckboxIndicator={showCheckboxIndicator}
      >
        {sortedItems.map((item) => {
          const originalIndex = items.indexOf(item);
          return (
            <NextDropdown.Item key={originalIndex.toString()} textValue={originalIndex.toString()}>
              <DropDownItemContainer $descriptionOnBottom={descriptionOnBottom}>
                <DropDownItemLeftContainer>
                  {showCheckboxIndicator && (
                    <CheckboxIndicatorBox>
                      <CheckIcon strokeWidth={3} />
                    </CheckboxIndicatorBox>
                  )}
                  {item.icon}
                  <DropDownItemText>{item.text}</DropDownItemText>
                  {item?.rightIcon}
                </DropDownItemLeftContainer>
                <DropDownItemDescription>{item.description}</DropDownItemDescription>
              </DropDownItemContainer>
              {originalIndex !== items.length - 1 && <Separator />}
            </NextDropdown.Item>
          );
        })}
      </DropDownMenu>
    ),
    [
      buttonWidth,
      disallowEmptySelection,
      disabledKeysLocal,
      sortedItems,
      items,
      multiselect,
      onLocalSelectionChange,
      selected,
      selectedValue,
      showCheckboxIndicator,
    ],
  );

  return (
    <Container {...rest}>
      {title && (
        <Title>
          {title}
          {required && ' *'}
          {helpText && <HelpTooltip $helpText={helpText} />}
        </Title>
      )}
      <StyledDropdown error={error} isDisabled={disabled} isBordered={!hideBorder}>
        {customTrigger ? (
          <StyledDropdown.Trigger>{customTrigger}</StyledDropdown.Trigger>
        ) : (
          <DropDownButton
            error={error}
            disabled={disabled || loading}
            ref={buttonRef}
            color={error ? 'error' : 'default'}
            isBordered={!hideBorder}
            iconRight={loading ? <Loading type="points-opacity" size="sm" /> : hideChevron ? <div /> : <ChevronIcon />}
          >
            <DropDownItemLeftContainer>
              {showSelectedValue ? selectedIcon : placeholderIcon}
              <DropDownValueText>
                {(showSelectedValue ? selectedValue || placeholder : placeholder) || ''}
                {shouldShowDescriptionWithValue && ` ${selectedDescription}`}
              </DropDownValueText>
              {showBadge && <Badge>1</Badge>}
              {selectedRightIcon}
            </DropDownItemLeftContainer>
          </DropDownButton>
        )}
        {dropDownComponent}
      </StyledDropdown>
      {error && <DropdownError>{error}</DropdownError>}
    </Container>
  );
};

export default Dropdown;
